import React, { useEffect, useState } from 'react';
import { pagesAPI, contactAPI, enrollmentAPI, uploadAPI, SITE_NAMES } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RichTextEditor } from '../ui/rich-text-editor';
import { toast } from 'sonner';
import { Save, MapPin, Phone, Mail, Clock, Upload, Trash2, Plus, FileText, Image } from 'lucide-react';

export const PagesAdmin = () => {
  const [activeSite, setActiveSite] = useState('ms');
  const [pages, setPages] = useState({ ms: {}, ds: {} });
  const [contactInfo, setContactInfo] = useState({ ms: {}, ds: {} });
  const [enrollment, setEnrollment] = useState({ ms: {}, ds: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [
        msHome, msAbout, msParents, msContact, msEnrollment,
        dsHome, dsAbout, dsParents, dsContact, dsEnrollment
      ] = await Promise.all([
        pagesAPI.getPage('ms', 'homepage').catch(() => ({ data: {} })),
        pagesAPI.getPage('ms', 'about').catch(() => ({ data: {} })),
        pagesAPI.getPage('ms', 'parents').catch(() => ({ data: {} })),
        contactAPI.getInfo('ms').catch(() => ({ data: {} })),
        enrollmentAPI.get('ms').catch(() => ({ data: {} })),
        pagesAPI.getPage('ds', 'homepage').catch(() => ({ data: {} })),
        pagesAPI.getPage('ds', 'about').catch(() => ({ data: {} })),
        pagesAPI.getPage('ds', 'parents').catch(() => ({ data: {} })),
        contactAPI.getInfo('ds').catch(() => ({ data: {} })),
        enrollmentAPI.get('ds').catch(() => ({ data: {} })),
      ]);
      
      setPages({
        ms: { homepage: msHome.data, about: msAbout.data, parents: msParents.data },
        ds: { homepage: dsHome.data, about: dsAbout.data, parents: dsParents.data }
      });
      setContactInfo({ ms: msContact.data, ds: dsContact.data });
      setEnrollment({ ms: msEnrollment.data, ds: dsEnrollment.data });
    } catch (error) {
      toast.error('Nepodarilo se nacíst data');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageKey, field, value) => {
    setPages(prev => ({
      ...prev,
      [activeSite]: {
        ...prev[activeSite],
        [pageKey]: { ...prev[activeSite][pageKey], [field]: value }
      }
    }));
  };

  const handleContactChange = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [activeSite]: { ...prev[activeSite], [field]: value }
    }));
  };

  const handleEnrollmentChange = (field, value) => {
    setEnrollment(prev => ({
      ...prev,
      [activeSite]: { ...prev[activeSite], [field]: value }
    }));
  };

  const savePage = async (pageKey) => {
    setSaving(true);
    try {
      await pagesAPI.updatePage(activeSite, pageKey, pages[activeSite][pageKey]);
      toast.success('Stránka byla uložena');
    } catch (error) {
      toast.error('Nepodarilo se uložit stránku');
    } finally {
      setSaving(false);
    }
  };

  const saveContactInfo = async () => {
    setSaving(true);
    try {
      await contactAPI.updateInfo(activeSite, contactInfo[activeSite]);
      toast.success('Kontaktní údaje byly uloženy');
    } catch (error) {
      toast.error('Nepodarilo se uložit kontaktní údaje');
    } finally {
      setSaving(false);
    }
  };

  const saveEnrollment = async () => {
    setSaving(true);
    try {
      const data = enrollment[activeSite];
      await enrollmentAPI.update(activeSite, {
        is_active: data.is_active || false,
        title: data.title || 'Zápis',
        content: data.content || '',
        poster_url: data.poster_url || null,
        documents: data.documents || []
      });
      toast.success('Nastavení zápisu uloženo');
    } catch (error) {
      toast.error('Nepodarilo se uložit nastavení zápisu');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, callback) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      callback(res.data.url);
      toast.success('Soubor nahrán');
    } catch (error) {
      toast.error('Nepodarilo se nahrát soubor');
    } finally {
      setUploading(false);
    }
  };

  const addEnrollmentDoc = () => {
    const docs = [...(enrollment[activeSite].documents || []), { title: '', file_url: '' }];
    handleEnrollmentChange('documents', docs);
  };

  const updateEnrollmentDoc = (index, field, value) => {
    const docs = [...(enrollment[activeSite].documents || [])];
    docs[index] = { ...docs[index], [field]: value };
    handleEnrollmentChange('documents', docs);
  };

  const removeEnrollmentDoc = (index) => {
    const docs = (enrollment[activeSite].documents || []).filter((_, i) => i !== index);
    handleEnrollmentChange('documents', docs);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  const sitePages = pages[activeSite] || {};
  const siteContact = contactInfo[activeSite] || {};
  const siteEnrollment = enrollment[activeSite] || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">Správa stránek</h1>
        <p className="text-[#64748B]">Úprava obsahu, hero sekce, zápisu a kontaktu</p>
      </div>

      <div className="flex gap-2 mb-4">
        {Object.entries(SITE_NAMES).map(([key, name]) => (
          <button
            key={key}
            onClick={() => setActiveSite(key)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              activeSite === key
                ? 'bg-[#4ECDC4] text-white shadow-md'
                : 'bg-white text-[#64748B] border border-slate-200 hover:border-[#4ECDC4]'
            }`}
            data-testid={`site-tab-${key}`}
          >
            {name}
          </button>
        ))}
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList className="bg-white border border-slate-100 p-1 rounded-xl flex-wrap">
          <TabsTrigger value="hero" className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white">
            Hero sekce
          </TabsTrigger>
          <TabsTrigger value="enrollment" className="rounded-lg data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white">
            Zápis
          </TabsTrigger>
          <TabsTrigger value="about" className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white">
            O nás
          </TabsTrigger>
          <TabsTrigger value="parents" className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white">
            Pro rodice
          </TabsTrigger>
          <TabsTrigger value="contact" className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white">
            Kontaktní údaje
          </TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
            <div className="text-xs text-[#64748B] bg-slate-50 px-3 py-1.5 rounded-full w-fit">
              Editujete: {SITE_NAMES[activeSite]}
            </div>
            <div className="space-y-2">
              <Label>Hero obrázek</Label>
              <div className="flex gap-2 items-center">
                {sitePages.homepage?.hero_image_url && (
                  <img src={sitePages.homepage.hero_image_url} alt="Hero" className="w-24 h-16 rounded-xl object-cover flex-shrink-0" />
                )}
                <label className="cursor-pointer flex-1">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, (url) => handlePageChange('homepage', 'hero_image_url', url))} />
                  <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                    <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : sitePages.homepage?.hero_image_url ? 'Zmenit obrázek' : 'Nahrát hero obrázek'}</span>
                  </Button>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Podtitulek hero sekce</Label>
              <Textarea
                value={sitePages.homepage?.hero_subtitle || ''}
                onChange={(e) => handlePageChange('homepage', 'hero_subtitle', e.target.value)}
                rows={3}
                className="rounded-xl resize-none"
                placeholder="Text pod hlavním nadpisem na úvodní stránce..."
                data-testid="hero-subtitle"
              />
            </div>
            <div className="space-y-2">
              <Label>Nadpis stránky</Label>
              <Input
                value={sitePages.homepage?.title || ''}
                onChange={(e) => handlePageChange('homepage', 'title', e.target.value)}
                className="rounded-xl"
                data-testid="homepage-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Meta popis (SEO)</Label>
              <Input
                value={sitePages.homepage?.meta_description || ''}
                onChange={(e) => handlePageChange('homepage', 'meta_description', e.target.value)}
                className="rounded-xl"
                data-testid="homepage-meta"
              />
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="text-sm font-medium text-[#2C3E50]">Plovoucí štítek na hero obrázku</div>
              <p className="text-xs text-[#64748B]">Malá kartička v levém dolním rohu hero obrázku (např. „3 třídy — Pro děti 3-6 let").</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Emoji / ikona</Label>
                  <Input
                    value={sitePages.homepage?.floating_emoji || ''}
                    onChange={(e) => handlePageChange('homepage', 'floating_emoji', e.target.value)}
                    placeholder={activeSite === 'ms' ? '👶' : '🏡'}
                    className="rounded-xl"
                    data-testid="homepage-floating-emoji"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hlavní text</Label>
                  <Input
                    value={sitePages.homepage?.floating_title || ''}
                    onChange={(e) => handlePageChange('homepage', 'floating_title', e.target.value)}
                    placeholder={activeSite === 'ms' ? '3 třídy' : 'Malý kolektiv'}
                    className="rounded-xl"
                    data-testid="homepage-floating-title"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Podtitulek</Label>
                  <Input
                    value={sitePages.homepage?.floating_subtitle || ''}
                    onChange={(e) => handlePageChange('homepage', 'floating_subtitle', e.target.value)}
                    placeholder={activeSite === 'ms' ? 'Pro děti 3-6 let' : 'Max. 12 dětí'}
                    className="rounded-xl"
                    data-testid="homepage-floating-subtitle"
                  />
                </div>
              </div>
            </div>

            <Button onClick={() => savePage('homepage')} disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="save-hero">
              <Save size={16} className="mr-2" />
              {saving ? 'Ukládání...' : 'Uložit hero sekci'}
            </Button>
          </div>
        </TabsContent>

        {/* Enrollment / Zápis */}
        <TabsContent value="enrollment">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
            <div className="text-xs text-[#64748B] bg-slate-50 px-3 py-1.5 rounded-full w-fit">
              Editujete: {SITE_NAMES[activeSite]}
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <div className="font-medium text-[#2C3E50]">Zápis je aktivní</div>
                <div className="text-sm text-[#64748B]">Zobrazí se v menu a na hero sekci</div>
              </div>
              <Switch
                checked={siteEnrollment.is_active || false}
                onCheckedChange={(checked) => handleEnrollmentChange('is_active', checked)}
                data-testid="enrollment-active-switch"
              />
            </div>

            <div className="space-y-2">
              <Label>Název stránky zápisu</Label>
              <Input
                value={siteEnrollment.title || ''}
                onChange={(e) => handleEnrollmentChange('title', e.target.value)}
                className="rounded-xl"
                placeholder="Zápis do MŠ"
                data-testid="enrollment-title"
              />
            </div>

            <div className="space-y-2">
              <Label>Text k zápisu</Label>
              <RichTextEditor
                value={siteEnrollment.content || ''}
                onChange={(html) => handleEnrollmentChange('content', html)}
                placeholder="Informace o zápisu, termíny, co s sebou..."
                testId="enrollment-content"
              />
            </div>

            <div className="space-y-2">
              <Label>Plakátek zápisu (JPG/PNG)</Label>
              <div className="flex gap-2 items-center">
                {siteEnrollment.poster_url && (
                  <img src={siteEnrollment.poster_url} alt="Plakátek" className="w-24 h-32 rounded-xl object-contain border flex-shrink-0" />
                )}
                <label className="cursor-pointer flex-1">
                  <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => handleFileUpload(e, (url) => handleEnrollmentChange('poster_url', url))} />
                  <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                    <span><Image size={16} className="mr-2" />{uploading ? 'Nahrávání...' : siteEnrollment.poster_url ? 'Zmenit plakátek' : 'Nahrát plakátek'}</span>
                  </Button>
                </label>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Dokumenty ke stažení</Label>
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addEnrollmentDoc} data-testid="add-enrollment-doc">
                  <Plus size={14} className="mr-1" /> Pridat dokument
                </Button>
              </div>
              {(siteEnrollment.documents || []).map((doc, index) => (
                <div key={index} className="flex gap-2 items-start p-3 bg-slate-50 rounded-xl">
                  <FileText size={20} className="text-[#64748B] mt-2 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Input
                      value={doc.title}
                      onChange={(e) => updateEnrollmentDoc(index, 'title', e.target.value)}
                      className="rounded-lg text-sm"
                      placeholder="Název dokumentu"
                      data-testid={`enrollment-doc-title-${index}`}
                    />
                    <div className="flex gap-2">
                      <label className="cursor-pointer flex-1">
                        <input type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => handleFileUpload(e, (url) => updateEnrollmentDoc(index, 'file_url', url))} />
                        <Button type="button" variant="outline" size="sm" className="w-full rounded-lg text-xs" disabled={uploading} asChild>
                          <span><Upload size={14} className="mr-1" />{uploading ? '...' : doc.file_url ? 'Zmenit soubor' : 'Nahrát soubor'}</span>
                        </Button>
                      </label>
                      {doc.file_url && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded text-xs text-green-700 flex-shrink-0">
                          <FileText size={12} /> Nahráno
                        </div>
                      )}
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeEnrollmentDoc(index)} className="text-red-500 hover:text-red-700 mt-1">
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>

            <Button onClick={saveEnrollment} disabled={saving} className="rounded-full bg-[#FF6B6B] hover:bg-[#E55A5A] text-white" data-testid="save-enrollment">
              <Save size={16} className="mr-2" />
              {saving ? 'Ukládání...' : 'Uložit nastavení zápisu'}
            </Button>
          </div>
        </TabsContent>

        {/* About & Parents */}
        {['about', 'parents'].map((pageKey) => (
          <TabsContent key={pageKey} value={pageKey}>
            <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
              <div className="text-xs text-[#64748B] bg-slate-50 px-3 py-1.5 rounded-full w-fit">
                Editujete: {SITE_NAMES[activeSite]}
              </div>
              <div className="space-y-2">
                <Label>Nadpis</Label>
                <Input
                  value={sitePages[pageKey]?.title || ''}
                  onChange={(e) => handlePageChange(pageKey, 'title', e.target.value)}
                  className="rounded-xl"
                  data-testid={`${pageKey}-title`}
                />
              </div>
              <div className="space-y-2">
                <Label>Obsah</Label>
                <RichTextEditor
                  value={sitePages[pageKey]?.content || ''}
                  onChange={(html) => handlePageChange(pageKey, 'content', html)}
                  testId={`${pageKey}-content`}
                />
              </div>
              <div className="space-y-2">
                <Label>Meta popis (SEO)</Label>
                <Input
                  value={sitePages[pageKey]?.meta_description || ''}
                  onChange={(e) => handlePageChange(pageKey, 'meta_description', e.target.value)}
                  className="rounded-xl"
                  data-testid={`${pageKey}-meta`}
                />
              </div>
              <Button onClick={() => savePage(pageKey)} disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid={`save-${pageKey}`}>
                <Save size={16} className="mr-2" />
                {saving ? 'Ukládání...' : 'Uložit zmeny'}
              </Button>
            </div>
          </TabsContent>
        ))}

        {/* Contact Info */}
        <TabsContent value="contact">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
            <div className="text-xs text-[#64748B] bg-slate-50 px-3 py-1.5 rounded-full w-fit">
              Editujete: {SITE_NAMES[activeSite]}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><MapPin size={14} /> Adresa</Label>
                <Input value={siteContact.address || ''} onChange={(e) => handleContactChange('address', e.target.value)} className="rounded-xl" data-testid="contact-address" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone size={14} /> Telefon</Label>
                <Input value={siteContact.phone || ''} onChange={(e) => handleContactChange('phone', e.target.value)} className="rounded-xl" data-testid="contact-phone" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail size={14} /> E-mail</Label>
                <Input type="email" value={siteContact.email || ''} onChange={(e) => handleContactChange('email', e.target.value)} className="rounded-xl" data-testid="contact-email" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Clock size={14} /> Provozní doba</Label>
                <Input value={siteContact.opening_hours || ''} onChange={(e) => handleContactChange('opening_hours', e.target.value)} className="rounded-xl" placeholder="Po-Pá: 6:30 - 16:30" data-testid="contact-hours" />
              </div>
            </div>
            <Button onClick={saveContactInfo} disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="save-contact">
              <Save size={16} className="mr-2" />
              {saving ? 'Ukládání...' : 'Uložit kontaktní údaje'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PagesAdmin;
